import isEmpty from "lodash/isEmpty";

import { TextTestID, WeightTrackingTestID } from "@/constants/TestID";
import BoldWeightText from "@/ui/components/vitals/BoldWeightText";
import EmptyWeightDataCard from "@/ui/components/vitals/EmptyWeightDataCard";
import WeightTrackingGraph from "@/ui/components/vitals/WeightTrackingGraph";
import { Text } from "@/ui/core";
import type { FlatListProps } from "@/ui/layout";
import { Box, TwoColumnList } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";
import { getObservedAtLong, getRoundedValue } from "@/utils/vitals/weight.util";

interface WeightTrackingHistoryProps {
  data: PatientObservationData[];
  onEndReached?: FlatListProps<PatientObservationData>["onEndReached"];
  onEndReachedThreshold?: FlatListProps<PatientObservationData>["onEndReachedThreshold"];
}

const WeightTrackingHistory = ({
  data,
  onEndReached,
  onEndReachedThreshold,
}: WeightTrackingHistoryProps) => {
  const baseTestID = WeightTrackingTestID.History;
  if (isEmpty(data)) {
    return (
      <Box padding={24}>
        <EmptyWeightDataCard testID={baseTestID} />
      </Box>
    );
  }
  return (
    <TwoColumnList
      data={data}
      header={
        <Box paddingBottom={24}>
          <WeightTrackingGraph data={data.slice(0, 10)} />
        </Box>
      }
      headerLeftItem={
        <Box width={96}>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(
              baseTestID,
              WeightTrackingTestID.ListHeader,
              "weight",
            )}
            variant="caption"
          >
            Weight
          </Text>
        </Box>
      }
      headerRightItem={
        <Text
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(
            baseTestID,
            WeightTrackingTestID.ListHeader,
            "collected",
          )}
          variant="caption"
        >
          Collected
        </Text>
      }
      keyExtractor={(item: PatientObservationData) =>
        item.observation.externalId
      }
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      renderLeftItem={({ item }) => (
        <Box width={96}>
          <BoldWeightText
            testID={baseTestID}
            text={item.observation.unit}
            weight={getRoundedValue(item.observation.value).toString()}
          />
        </Box>
      )}
      renderRightItem={({ item }) => (
        <Text
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(baseTestID, TextTestID.Date)}
          variant="body"
        >
          {getObservedAtLong(item)}
        </Text>
      )}
      testID={baseTestID}
    />
  );
};

export type { WeightTrackingHistoryProps };
export default WeightTrackingHistory;
